import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon, RepeatIcon } from "@chakra-ui/icons";
import Select from "react-select";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../Context/UserContext";
import { ChatService } from "@/services/chatService";
import { useChatSocket } from "../../hooks/useChatSocket";

export default function ChatWidgetV2({ clinicaId, children }) {
  const toast = useToast();
  const { user } = useUser();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedPeerKey, setSelectedPeerKey] = useState("");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedContactOption, setSelectedContactOption] = useState(null);

  const { isOpen: isNewChatOpen, onOpen: onOpenNewChat, onClose: onCloseNewChat } = useDisclosure();
  const messagesEndRef = useRef(null);

  const bg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const muted = useColorModeValue("gray.600", "gray.300");
  const panelShadow = useColorModeValue("lg", "dark-lg");
  const msgOtherBg = useColorModeValue("gray.100", "gray.700");
  const msgOtherColor = useColorModeValue("gray.900", "gray.100");

  const {
    socketStatus,
    lastSocketError,
    me,
    onlineUsers,
    messagesByPeer,
    threads,
    loadHistory,
    sendMessage,
    rejoinClinic,
    addThread,
  } = useChatSocket({ isOpen, clinicaId, token: user?.token });

  // Contatos carregados via React Query — sem fetch manual, sem estado local
  const { data: rawContacts = [], isLoading: contactsLoading } = useQuery({
    queryKey: ["chat_contacts", clinicaId],
    queryFn: () => ChatService.getUsersByChat(clinicaId),
    enabled: !!clinicaId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  // Escuta evento externo que abre o widget
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("chat:open", handler);
    return () => window.removeEventListener("chat:open", handler);
  }, []);

  // Scroll para última mensagem
  useEffect(() => {
    if (!isOpen || !selectedPeerKey) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedPeerKey, messagesByPeer[selectedPeerKey]?.length]);

  // Limpa estado de UI ao fechar
  useEffect(() => {
    if (isOpen) return;
    setSelectedPeerKey("");
    setSearch("");
    setDraft("");
    setSending(false);
    setSelectedContactOption(null);
  }, [isOpen]);

  const onlineSet = useMemo(() => {
    const set = new Set();
    (Array.isArray(onlineUsers) ? onlineUsers : []).forEach((u) => {
      if (u?.userKey) set.add(u.userKey);
    });
    return set;
  }, [onlineUsers]);

  const onlineCount = useMemo(() => {
    const meKey = me?.userKey;
    return [...onlineSet].filter((k) => k !== meKey).length;
  }, [onlineSet, me?.userKey]);

  const isPeerOnline = (peerKey) => onlineSet.has(String(peerKey || ""));

  const filteredThreads = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    const meKey = me?.userKey;
    return (Array.isArray(threads) ? threads : [])
      .filter((t) => t?.peerUserKey && t.peerUserKey !== meKey)
      .filter((t) => !q || [t?.label, t?.peerUserKey, t?.lastText].some((v) => String(v || "").toLowerCase().includes(q)))
      .sort((a, b) => String(b.lastTs || "").localeCompare(String(a.lastTs || "")));
  }, [threads, search, me?.userKey]);

  const selectedPeer = useMemo(() => {
    if (!selectedPeerKey) return null;
    const online = onlineUsers.find((u) => u.userKey === selectedPeerKey);
    if (online) return online;
    const thread = (Array.isArray(threads) ? threads : []).find((t) => t?.peerUserKey === selectedPeerKey);
    if (thread) return { userKey: selectedPeerKey, label: thread.label };
    return { userKey: selectedPeerKey, label: selectedPeerKey };
  }, [onlineUsers, threads, selectedPeerKey]);

  const selectedMessages = useMemo(() => {
    const list = messagesByPeer[selectedPeerKey];
    return Array.isArray(list) ? list : [];
  }, [messagesByPeer, selectedPeerKey]);

  // Opções para o react-select — exclui o próprio usuário pelo userKey OU pelo id (fallback antes do session:me chegar)
  const contactOptions = useMemo(() => {
    const myKey = me?.userKey;
    const myId = user?._id;
    return (Array.isArray(rawContacts) ? rawContacts : [])
      .filter((c) => {
        if (!c?.userKey) return false;
        if (myKey && c.userKey === myKey) return false;
        if (!myKey && myId && c.id === String(myId)) return false;
        return true;
      })
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")))
      .map((c) => ({ value: c.userKey, label: c.label || c.userKey, contact: c }));
  }, [rawContacts, me?.userKey, user?._id]);

  const canSend = !!selectedPeerKey && !!clinicaId && String(draft || "").trim().length > 0 && !sending;

  const handleSelectPeer = (peerKey) => {
    setSelectedPeerKey(peerKey);
    loadHistory(peerKey);
  };

  const openNewChat = () => {
    if (!clinicaId) {
      toast({ title: "Selecione uma clínica", description: "O chat é por clínica.", status: "info", duration: 4000, isClosable: true });
      return;
    }
    setSelectedContactOption(null);
    onOpenNewChat();
  };

  const handleConfirmNewChat = () => {
    const contact = selectedContactOption?.contact;
    if (!contact?.userKey) return;
    addThread(contact.userKey, contact.label || contact.userKey);
    setSelectedPeerKey(contact.userKey);
    loadHistory(contact.userKey);
    setSelectedContactOption(null);
    onCloseNewChat();
  };

  const handleSend = () => {
    if (!canSend) return;
    const text = String(draft || "").trim();
    setSending(true);
    sendMessage({
      toUserKey: selectedPeerKey,
      text,
      onSuccess: () => { setSending(false); setDraft(""); },
      onError: (err) => {
        setSending(false);
        toast({ title: "Não foi possível enviar", description: err, status: "error", duration: 4500, isClosable: true });
      },
    });
  };

  const statusLabel =
    socketStatus === "conectado" ? "Conectado"
      : socketStatus === "conectando" ? "Conectando..."
        : socketStatus === "erro" ? `Erro: ${lastSocketError || "desconhecido"}`
          : "Desconectado";

  const toggle = () => setIsOpen((v) => !v);

  return (
    <>
      {typeof children === "function" ? children({ onOpen: toggle, isOpen, onlineCount }) : children}

      {isOpen && (
        <Box
          position="fixed"
          right="80px"
          bottom="80px"
          w={{ base: "calc(100vw - 96px)", md: "560px" }}
          h={{ base: "70vh", md: "520px" }}
          bg={bg}
          borderWidth="1px"
          borderColor={border}
          borderRadius="md"
          boxShadow={panelShadow}
          zIndex={1200}
          overflow="hidden"
        >
          {/* Cabeçalho */}
          <Flex align="center" justify="space-between" p={3}>
            <VStack spacing={0} align="start">
              <Text fontWeight="bold">Chat interno</Text>
              <HStack spacing={2}>
                <Text fontSize="xs" color={socketStatus === "erro" ? "red.500" : muted}>
                  {statusLabel}
                </Text>
                {clinicaId ? (
                  <Text fontSize="xs" color={muted}>Clínica: {String(clinicaId).slice(-6)}</Text>
                ) : (
                  <Text fontSize="xs" color="orange.500">Selecione uma clínica</Text>
                )}
              </HStack>
            </VStack>
            <HStack>
              <Tooltip label="Reentrar na clínica / atualizar online">
                <IconButton aria-label="Atualizar presença" size="sm" icon={<RepeatIcon />} onClick={rejoinClinic} />
              </Tooltip>
              <IconButton aria-label="Fechar" size="sm" icon={<CloseIcon />} onClick={() => setIsOpen(false)} />
            </HStack>
          </Flex>
          <Divider />

          <Flex h="calc(100% - 57px)">
            {/* Lista de conversas */}
            <Box
              w={{ base: "180px", md: "220px" }}
              borderRightWidth="1px"
              borderColor={border}
              p={3}
              overflow="auto"
            >
              <Flex align="center" justify="space-between" mb={2} gap={2}>
                <Text fontSize="sm" fontWeight="semibold">
                  Conversas <Badge ml={1}>{filteredThreads.length}</Badge>
                </Text>
                <Tooltip label="Novo chat">
                  <IconButton
                    aria-label="Novo chat"
                    size="xs"
                    icon={<AddIcon />}
                    onClick={openNewChat}
                    isDisabled={!clinicaId}
                  />
                </Tooltip>
              </Flex>
              <Input
                size="sm"
                placeholder="Buscar conversa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                mb={3}
              />

              {socketStatus === "conectando" && (
                <HStack color={muted} mb={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Conectando...</Text>
                </HStack>
              )}

              {filteredThreads.length === 0 ? (
                <Text fontSize="sm" color={muted}>
                  {clinicaId ? 'Clique em "Novo chat" para começar.' : "Selecione uma clínica."}
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {filteredThreads.map((t) => {
                    const isSelected = selectedPeerKey === t.peerUserKey;
                    const online = isPeerOnline(t.peerUserKey);
                    return (
                      <Button
                        key={`thread-${t.peerUserKey}`}
                        size="sm"
                        variant={isSelected ? "solid" : "ghost"}
                        colorScheme={isSelected ? "teal" : "gray"}
                        justifyContent="flex-start"
                        onClick={() => handleSelectPeer(t.peerUserKey)}
                        whiteSpace="normal"
                        textAlign="left"
                        height="auto"
                        py={2}
                      >
                        <HStack align="start" spacing={2} w="full">
                          <Box
                            w="8px"
                            h="8px"
                            borderRadius="full"
                            bg={online ? "green.400" : "gray.400"}
                            mt="6px"
                            flexShrink={0}
                          />
                          <Box flex="1" minW="0">
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                              {t.label || t.peerUserKey}
                            </Text>
                            <Text fontSize="xs" color={isSelected ? "whiteAlpha.800" : muted} noOfLines={1}>
                              {t.lastText || "Sem mensagens"}
                            </Text>
                          </Box>
                        </HStack>
                      </Button>
                    );
                  })}
                </VStack>
              )}
            </Box>

            {/* Painel de mensagens */}
            <Flex flex="1" direction="column">
              <Box p={3} borderBottomWidth="1px" borderColor={border}>
                {selectedPeer ? (
                  <VStack spacing={0} align="start">
                    <Text fontWeight="bold" noOfLines={1}>{selectedPeer.label || selectedPeer.userKey}</Text>
                    <Text fontSize="xs" color={muted} noOfLines={1}>{selectedPeer.userKey}</Text>
                  </VStack>
                ) : (
                  <Text color={muted}>Selecione uma conversa para começar.</Text>
                )}
              </Box>

              <Box flex="1" p={3} overflow="auto">
                {selectedPeerKey ? (
                  <VStack align="stretch" spacing={2}>
                    {selectedMessages.map((m, idx) => {
                      const isMine = m.fromUserKey === me?.userKey;
                      return (
                        <Box
                          key={m.id || `${m.ts}-${idx}`}
                          alignSelf={isMine ? "flex-end" : "flex-start"}
                          maxW="85%"
                          bg={isMine ? "teal.500" : msgOtherBg}
                          color={isMine ? "white" : msgOtherColor}
                          borderRadius="md"
                          px={3}
                          py={2}
                        >
                          <Text fontSize="sm" whiteSpace="pre-wrap">{m.text}</Text>
                          <Text fontSize="xs" opacity={0.75} mt={1}>
                            {new Date(m.ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                          </Text>
                        </Box>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </VStack>
                ) : (
                  <Text fontSize="sm" color={muted}>
                    O chat é por clínica. Suas conversas aparecem à esquerda.
                  </Text>
                )}
              </Box>

              <Divider />
              <Box p={3}>
                <Textarea
                  size="sm"
                  placeholder={selectedPeerKey ? "Digite sua mensagem..." : "Selecione uma conversa..."}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  isDisabled={!selectedPeerKey}
                  resize="none"
                  minH="70px"
                  maxLength={1000}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                  }}
                />
                <Flex mt={2} justify="space-between" align="center">
                  <Text fontSize="xs" color={muted}>Enter envia • Shift+Enter quebra linha</Text>
                  <Button size="sm" colorScheme="teal" onClick={handleSend} isDisabled={!canSend} isLoading={sending}>
                    Enviar
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Modal novo chat */}
      <Modal isOpen={isNewChatOpen} onClose={onCloseNewChat} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader><Heading size="md">Novo chat</Heading></ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            <Text fontSize="sm" color={muted} mb={3}>
              Digite o nome do usuário para buscar e selecione para iniciar.
            </Text>
            {contactsLoading ? (
              <HStack color={muted} py={6} justify="center">
                <Spinner size="sm" />
                <Text fontSize="sm">Carregando contatos...</Text>
              </HStack>
            ) : (
              <Select
                options={contactOptions}
                value={selectedContactOption}
                onChange={setSelectedContactOption}
                placeholder="Buscar usuário..."
                noOptionsMessage={() => "Nenhum usuário encontrado"}
                isClearable
                isSearchable
                menuPosition="fixed"
                menuShouldBlockScroll
                formatOptionLabel={(opt) => {
                  const isOnline = onlineSet.has(opt.value);
                  return (
                    <HStack spacing={2}>
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={isOnline ? "green.400" : "gray.400"}
                        flexShrink={0}
                      />
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold">{opt.label}</Text>
                        <Text fontSize="xs" color="gray.500">{isOnline ? "online" : "offline"}</Text>
                      </Box>
                    </HStack>
                  );
                }}
                styles={{
                  control: (base) => ({ ...base, fontSize: "14px", minHeight: "38px" }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? "#319795" : state.isFocused ? "#E6FFFA" : "white",
                    color: state.isSelected ? "white" : "inherit",
                    cursor: "pointer",
                  }),
                }}
              />
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onCloseNewChat}>Cancelar</Button>
            <Button
              isDisabled={!selectedContactOption}
              onClick={handleConfirmNewChat}
            >
              Iniciar chat
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
